import { Dialog, Spinner, toast } from '@scholastic/mockingjay';
import { useState } from 'react';
import {
  getImportGoogleClassesSuccessToastMessage,
  hasImportGoogleClassesIssues,
  ImportGoogleClassesContent,
  ImportGoogleClassesIssuesDialog,
  type ImportGoogleClassesContentProps,
  type GoogleCourse,
  useImportGoogleClassesApi,
} from 'eds-roster-ux/import-google-classes-content';

interface ImportGoogleClassesDialogProps {
  defaultOpen?: boolean;
  onClose?: () => void;
  userName: string;
  classes?: ImportGoogleClassesContentProps['classes'];
}

const hiddenTrigger = (
  <button type="button" className="sr-only" tabIndex={-1} aria-hidden>
    Open Google classes import dialog
  </button>
);

export function ImportGoogleClassesDialog({
  defaultOpen = false,
  onClose,
  userName,
  classes,
}: ImportGoogleClassesDialogProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const [isIssuesDialogOpen, setIsIssuesDialogOpen] = useState(false);
  const {
    data: importResult = null,
    isPending: isImporting,
    mutateAsync: importGoogleClasses,
    reset,
  } = useImportGoogleClassesApi();

  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
  };

  const handleCancel = () => {
    if (isImporting) {
      return;
    }

    handleClose();
  };

  const handleImport = async (selectedClasses: GoogleCourse[]) => {
    setIsOpen(false);

    try {
      const response = await importGoogleClasses(selectedClasses);

      if (hasImportGoogleClassesIssues(response)) {
        setIsIssuesDialogOpen(true);
        return;
      }

      toast.success(getImportGoogleClassesSuccessToastMessage(response), {
        position: 'top-center',
      });
      reset();
      onClose?.();
    } catch {
      toast.error('Unable to import classes. Please try again.', {
        position: 'top-center',
      });
      onClose?.();
    }
  };

  const handleIssuesDialogOpenChange = (isIssuesOpen: boolean) => {
    setIsIssuesDialogOpen(isIssuesOpen);

    if (!isIssuesOpen) {
      setTimeout(reset, 300);
      onClose?.();
    }
  };

  const handleOpenChange = (isDialogOpen: boolean) => {
    if (isDialogOpen || isImporting) {
      return;
    }

    handleClose();
  };

  return (
    <>
      {isImporting && (
        <Spinner variant="modal" size="xl" label="Importing classes..." />
      )}
      <Dialog
        open={isOpen}
        onOpenChange={handleOpenChange}
        trigger={hiddenTrigger}
        title="Import classes"
        description=""
      >
        <div className="w-[calc(100vw-3rem)] min-w-[350px] max-w-[604px]">
          <ImportGoogleClassesContent
            open={isOpen}
            userName={userName}
            classes={classes}
            onCancel={handleCancel}
            onImport={handleImport}
            isImporting={isImporting}
            variant="dialog"
          />
        </div>
      </Dialog>
      <ImportGoogleClassesIssuesDialog
        open={isIssuesDialogOpen}
        onOpenChange={handleIssuesDialogOpenChange}
        result={importResult}
        title="Import issues"
      />
    </>
  );
}
